import { baseApi, API_BASE_URL } from "./baseApi";
import type { UpdateAdminProfilePayload } from "../types/common";

export interface AdminApiResponse<T = unknown> {
  success: boolean;
  message: string;
  data: T;
  errors: string[];
}

export interface AdminProfile {
  id: number;
  name: string;
  email: string;
  phone: string;
  role: string;
  city: string;
  isVerified: boolean;
  avatarUrl: string | null;
}

const normalizeProfile = (raw: Record<string, any> | undefined): AdminProfile => ({
  id: Number(raw?.id ?? 0),
  name: String(raw?.name ?? ""),
  email: String(raw?.email ?? ""),
  phone: String(raw?.phone ?? ""),
  role: String(raw?.role ?? "Admin"),
  city: String(raw?.city ?? ""),
  isVerified: Boolean(raw?.isVerified ?? false),
  avatarUrl: raw?.avatarUrl ? String(raw.avatarUrl) : null,
});

export const getFullAvatarUrl = (relativeUrl: string | null): string | null => {
  if (!relativeUrl) return null;
  if (relativeUrl.startsWith("http")) return relativeUrl;
  // Strip the "/api" suffix from API_BASE_URL to get the host, then append the path
  const host = API_BASE_URL.replace(/\/api$/, "");
  return `${host}${relativeUrl}`;
};

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    fetchAdminProfile: builder.query<AdminProfile, void>({
      query: () => ({ url: "/Admin/profile", method: "GET" }),
      providesTags: ["AdminProfile"],
      transformResponse: (response: AdminApiResponse<unknown> | unknown) => {
        const data = (response as AdminApiResponse<unknown>)?.data ?? response;
        return normalizeProfile(data as Record<string, any>);
      },
    }),

    updateAdminProfile: builder.mutation<AdminProfile, UpdateAdminProfilePayload>({
      query: (payload) => ({
        url: "/Admin/profile",
        method: "PUT",
        body: { name: payload.name, phone: payload.phone, role: payload.role },
      }),
      invalidatesTags: ["AdminProfile"],
      transformResponse: (response: AdminApiResponse<unknown> | unknown) => {
        const data = (response as AdminApiResponse<unknown>)?.data ?? response;
        return normalizeProfile(data as Record<string, any>);
      },
    }),

    uploadAdminAvatar: builder.mutation<AdminProfile, File>({
      query: (file) => {
        const form = new FormData();
        form.append("file", file);
        return { url: "/Admin/profile/avatar", method: "POST", body: form };
      },
      invalidatesTags: ["AdminProfile"],
      transformResponse: (response: AdminApiResponse<unknown> | unknown) => {
        const data = (response as AdminApiResponse<unknown>)?.data ?? response;
        return normalizeProfile(data as Record<string, any>);
      },
    }),

    deleteAdminAvatar: builder.mutation<AdminProfile, void>({
      query: () => ({ url: "/Admin/profile/avatar", method: "DELETE" }),
      invalidatesTags: ["AdminProfile"],
      transformResponse: (response: AdminApiResponse<unknown> | unknown) => {
        const data = (response as AdminApiResponse<unknown>)?.data ?? response;
        return normalizeProfile(data as Record<string, any>);
      },
    }),
  }),
});

export const {
  useFetchAdminProfileQuery,
  useUpdateAdminProfileMutation,
  useUploadAdminAvatarMutation,
  useDeleteAdminAvatarMutation,
} = adminApi;
